<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowRight, Check, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck, Sparkles } from '@lucide/vue'
import { ApiError, api, postJson } from '../api'
import { auth } from '../state'
import type { AuthUser } from '../types'
import brandLogo from '../assets/brand/logo-lumiscale-final.svg'

const router = useRouter()
const loading = ref(false)
const error = ref('')
const showPassword = ref(false)
const needsTotp = ref(false)
const captcha = ref<{ token: string; question: string; answer: string } | null>(null)
const form = ref({ email: '', password: '', totpCode: '' })
const mode = ref<'login' | 'forgot'>('login')
const recoveryEmail = ref('')
const recoveryMessage = ref('')
const recoveryError = ref('')
const recoveryLoading = ref(false)
const capabilities = ref({ passwordResetEnabled: false })

onMounted(async () => {
  try { capabilities.value = await api<typeof capabilities.value>('/api/auth/capabilities') } catch { /* Login remains available if this informational request fails. */ }
})

async function loadCaptcha() {
  const challenge = await api<{ token: string; question: string }>('/api/auth/captcha?purpose=login')
  captcha.value = { ...challenge, answer: '' }
}

async function submit() {
  loading.value = true
  error.value = ''
  try {
    const result = await postJson<{ user: AuthUser }>('/api/auth/admin/login', {
      email: form.value.email,
      password: form.value.password,
      ...(form.value.totpCode ? { totpCode: form.value.totpCode } : {}),
      ...(captcha.value ? { captchaToken: captcha.value.token, captchaAnswer: captcha.value.answer } : {}),
    })
    auth.user = result.user
    auth.ready = true
    await router.push(result.user.mustChangePassword ? '/account?force-password-change=1' : !result.user.totpEnabled ? '/account?setup-2fa=1' : '/admin')
  } catch (err) {
    const code = err instanceof ApiError ? (err.details as { code?: string })?.code : undefined
    if (code === 'TOTP_REQUIRED') needsTotp.value = true
    if (code === 'CAPTCHA_REQUIRED') await loadCaptcha()
    error.value = err instanceof ApiError ? err.message : '暂时无法登录，请稍后重试。'
  } finally {
    loading.value = false
  }
}

async function requestPasswordReset() {
  recoveryLoading.value = true
  recoveryError.value = ''
  recoveryMessage.value = ''
  try {
    const result = await postJson<{ ok: boolean; message?: string }>('/api/auth/password/forgot', { email: recoveryEmail.value })
    recoveryMessage.value = result.message || '如果该邮箱已登记，重置链接会很快发送。'
  } catch (err) {
    recoveryError.value = err instanceof ApiError ? err.message : '暂时无法发起找回，请稍后重试或联系站长。'
  } finally {
    recoveryLoading.value = false
  }
}
</script>

<template>
  <div class="auth-page section-pad">
    <section class="auth-story">
      <p class="eyebrow"><Sparkles :size="14" />LUMISCALE ADMIN</p>
      <h1>把温柔留给来访者，<br/>把边界交给守护者。</h1>
      <p>公开问卷的浏览与填写无需登录；本地创作也不会把内容上传到平台。这里仅供站长和管理员处理审核、发布与数据安全事务。</p>
      <div class="auth-art" aria-hidden="true"><span class="auth-ring ring-one"/><span class="auth-ring ring-two"/><div class="auth-core"><img :src="brandLogo" alt="" /></div></div>
      <ul><li><Check />管理员账号独立授权</li><li><Check />敏感操作写入审计记录</li><li><Check />支持两步验证与设备撤销</li></ul>
    </section>

    <section class="auth-card">
      <div class="auth-tabs single-tab"><span class="active">站长 / 管理员入口</span></div>
      <div class="security-notice"><ShieldCheck/><p>普通浏览者、填写者和本地创作者无需账号。若你不是管理员，请返回公开区域使用对应功能。</p></div>
      <form v-if="mode === 'login'" @submit.prevent="submit">
        <label class="field-label"><span>管理员邮箱</span><div class="input-wrap"><Mail /><input v-model="form.email" type="email" autocomplete="username" placeholder="admin@example.com" required /></div></label>
        <label class="field-label"><span>密码</span><div class="input-wrap"><LockKeyhole /><input v-model="form.password" :type="showPassword ? 'text' : 'password'" autocomplete="current-password" placeholder="输入管理员密码" minlength="8" required /><button type="button" class="password-toggle" :title="showPassword ? '隐藏密码' : '显示密码'" :aria-label="showPassword ? '隐藏密码' : '显示密码'" @click="showPassword = !showPassword"><EyeOff v-if="showPassword"/><Eye v-else/></button></div></label>
        <label v-if="needsTotp" class="field-label"><span>两步验证码</span><div class="input-wrap"><ShieldCheck/><input v-model="form.totpCode" inputmode="numeric" maxlength="6" autocomplete="one-time-code" placeholder="6 位动态验证码" required/></div></label>
        <label v-if="captcha" class="field-label"><span>人机验证：{{ captcha.question }}</span><div class="input-wrap"><ShieldCheck/><input v-model="captcha.answer" inputmode="numeric" autocomplete="off" placeholder="填写计算结果" required/></div></label>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="button button-dark full-button" :disabled="loading">{{ loading ? '正在核验…' : '进入站长管理台' }} <ArrowRight v-if="!loading"/></button>
      </form>
      <form v-else @submit.prevent="requestPasswordReset">
        <p class="auth-recovery-copy">输入管理员邮箱。为避免泄露账号状态，系统只会显示统一的发送结果；重置链接有效期为 1 小时。</p>
        <label class="field-label"><span>管理员邮箱</span><div class="input-wrap"><Mail /><input v-model="recoveryEmail" type="email" autocomplete="email" placeholder="admin@example.com" required /></div></label>
        <p v-if="recoveryError" class="form-error" role="alert">{{ recoveryError }}</p>
        <p v-if="recoveryMessage" class="dashboard-message" role="status">{{ recoveryMessage }}</p>
        <button class="button button-dark full-button" :disabled="recoveryLoading">{{ recoveryLoading ? '正在处理…' : '发送重置链接' }} <ArrowRight v-if="!recoveryLoading"/></button>
      </form>
      <div class="auth-links">
        <button type="button" class="forgot-link" @click="mode = mode === 'login' ? 'forgot' : 'login'; recoveryError = ''; recoveryMessage = ''">{{ mode === 'login' ? '忘记管理员密码？' : '返回管理员登录' }}</button>
        <RouterLink class="forgot-link" to="/local-create">不是管理员？前往本地创作模式</RouterLink>
      </div>
      <small v-if="mode === 'forgot' && !capabilities.passwordResetEnabled" class="auth-note">邮件找回尚未启用。请联系超级管理员在后台重置临时密码；超级管理员遗失密码时，请按部署手册执行服务器应急恢复。</small>
      <small v-else class="auth-note">临时密码首次使用后必须更换；建议随后立即开启两步验证。</small>
    </section>
  </div>
</template>
